import { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { getSessionFromCookie } from "../utils/cognito";
import { toast } from "react-toastify";
import { SuccessToast } from "../ui/toastResponds/SuccessToast";
import { ErrorToast } from "../ui/toastResponds/ErrorToast";
import { toastConfig } from "../config/toast.config";
import Spinner from "../ui/Spinner";

// Flex proposals key on a contract id per sub-API; commodity proposals key on
// {ean, company_id, direction} (backend: POST /contracts/send-proposal).
export type ProposalTarget =
  | { kind: "flex"; apiPath: "site-steering" | "bess"; contractId: number }
  | { kind: "commodity"; ean: string; companyId: number; direction: "injection" | "offtake" | "combined" };

interface SendProposalModalProps {
  isOpen: boolean;
  onClose: () => void;
  target: ProposalTarget;
}

const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

export const SendProposalModal = ({ isOpen, onClose, target }: SendProposalModalProps) => {
  const { t } = useTranslation("ActiveServices");
  const [signatoryEmail, setSignatoryEmail] = useState("");
  const [emailsLoading, setEmailsLoading] = useState(false);
  const [additionalRecipients, setAdditionalRecipients] = useState<string[]>([""]);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [signatoryError, setSignatoryError] = useState<string | null>(null);
  const [recipientErrors, setRecipientErrors] = useState<Record<number, string>>({});
  const [messageError, setMessageError] = useState<string | null>(null);

  const targetKey =
    target.kind === "flex"
      ? `flex:${target.apiPath}:${target.contractId}`
      : `commodity:${target.ean}:${target.companyId}:${target.direction}`;

  useEffect(() => {
    if (!isOpen) return;

    setSignatoryEmail("");
    setAdditionalRecipients([""]);
    setMessage("");
    setShowConfirm(false);
    setEmailsLoading(true);
    setSignatoryError(null);
    setRecipientErrors({});
    setMessageError(null);

    const session = getSessionFromCookie();
    const token = session?.getAccessToken().getJwtToken();
    const controller = new AbortController();
    const headers = { Authorization: `Bearer ${token}` };

    const prefill =
      target.kind === "flex"
        ? axios
            .get(
              `${import.meta.env.VITE_API_SERVICES}/api/v1/sdp/${target.apiPath}/${target.contractId}/billing-info`,
              { headers, signal: controller.signal },
            )
            .then((res) => {
              const companyEmail = res.data.email ?? "";
              setAdditionalRecipients(companyEmail ? [companyEmail] : [""]);
            })
        : axios
            .get(`${import.meta.env.VITE_API_SERVICES}/api/v1/contracts/billing-info`, {
              headers,
              signal: controller.signal,
              params: { ean: target.ean, company_id: target.companyId, direction: target.direction },
            })
            .then((res) => {
              const companyEmail = (res.data.companyEmail ?? "").trim();
              const billingEmail = (res.data.billingEmail ?? "").trim();
              setSignatoryEmail(companyEmail);
              setAdditionalRecipients(
                billingEmail && billingEmail !== companyEmail ? [billingEmail] : [""],
              );
            });

    prefill
      .catch((err) => {
        if (!axios.isCancel(err)) {
          toast(<ErrorToast title={t("sendProposal.errorTitle")} message={t("sendProposal.loadRecipientsError")} />, {
            ...toastConfig,
            autoClose: 5000,
            icon: false,
          });
        }
      })
      .finally(() => setEmailsLoading(false));

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, targetKey]);

  if (!isOpen) return null;

  const updateRecipient = (index: number, value: string) => {
    setAdditionalRecipients((prev) => prev.map((e, i) => (i === index ? value : e)));
    setRecipientErrors((prev) => {
      if (!(index in prev)) return prev;
      return Object.fromEntries(
        Object.entries(prev).filter(([k]) => Number(k) !== index),
      );
    });
  };

  const addRecipient = () => setAdditionalRecipients((prev) => [...prev, ""]);

  const removeRecipient = (index: number) => {
    setAdditionalRecipients((prev) =>
      prev.length === 1 ? [""] : prev.filter((_, i) => i !== index),
    );
    setRecipientErrors({});
  };

  const trimmedSignatory = signatoryEmail.trim();
  const signatoryValid = isValidEmail(trimmedSignatory);
  const allRecipientsValid = additionalRecipients.every((e) => {
    const trimmed = e.trim();
    return trimmed === "" || isValidEmail(trimmed);
  });
  const canSend = signatoryValid && allRecipientsValid && !emailsLoading && !sending;

  const handleSendClick = () => {
    if (!canSend) return;
    setShowConfirm(true);
  };

  const handleConfirmSend = async () => {
    setSending(true);
    setSignatoryError(null);
    setRecipientErrors({});
    setMessageError(null);
    try {
      const session = getSessionFromCookie();
      const token = session?.getAccessToken().getJwtToken();

      if (!token) {
        toast(<ErrorToast title={t("sendProposal.errorTitle")} message={t("sendProposal.sessionExpired")} />, {
          ...toastConfig,
          autoClose: 5000,
          icon: false,
        });
        return;
      }

      const recipients = additionalRecipients.map((e) => e.trim()).filter(Boolean);
      const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

      if (target.kind === "flex") {
        await axios.post(
          `${import.meta.env.VITE_API_SERVICES}/api/v1/sdp/${target.apiPath}/send-proposal`,
          { contract_id: target.contractId, main_recipient: signatoryEmail, additional_recipients: recipients, message },
          { headers },
        );
      } else {
        await axios.post(
          `${import.meta.env.VITE_API_SERVICES}/api/v1/contracts/send-proposal`,
          {
            ean: target.ean,
            company_id: target.companyId,
            direction: target.direction,
            main_recipient: signatoryEmail,
            additional_recipients: recipients,
            message,
          },
          { headers },
        );
      }

      toast(<SuccessToast title={t("sendProposal.successTitle")} message={t("sendProposal.sendSuccess")} />, {
        ...toastConfig,
        autoClose: 5000,
        icon: false,
      });
      setShowConfirm(false);
      onClose();
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const data = err.response?.data as
          | { message?: string; field?: string; errors?: Record<string, string | string[]> }
          | undefined;

        const structured =
          data?.errors ?? (data?.field ? { [data.field]: data.message ?? "" } : null);

        if (structured) {
          let routed = false;
          const nextRecipientErrors: Record<number, string> = {};
          for (const [field, raw] of Object.entries(structured)) {
            const msg = Array.isArray(raw) ? raw.join(" ") : raw;
            if (!msg) continue;
            if (field === "main_recipient" || field === "signatoryEmail") {
              setSignatoryError(msg);
              routed = true;
            } else if (
              field === "additional_recipients" ||
              field.startsWith("additional_recipients")
            ) {
              const indexMatch = field.match(/\.(\d+)$/);
              if (indexMatch) {
                nextRecipientErrors[Number(indexMatch[1])] = msg;
              } else {
                additionalRecipients.forEach((e, i) => {
                  if (e.trim()) nextRecipientErrors[i] = msg;
                });
              }
              routed = true;
            } else if (field === "message" || field === "body") {
              setMessageError(msg);
              routed = true;
            }
          }
          if (Object.keys(nextRecipientErrors).length > 0) {
            setRecipientErrors(nextRecipientErrors);
          }
          if (routed) {
            setShowConfirm(false);
            return;
          }
        }

        const text = data?.message ?? "";
        const lower = text.toLowerCase();
        if (
          text &&
          (lower.includes("email") || lower.includes("recipient") || lower.includes("address"))
        ) {
          const firstNonEmpty = additionalRecipients.findIndex((e) => e.trim() !== "");
          if (firstNonEmpty === -1) {
            setSignatoryError(text);
          } else {
            setRecipientErrors({ [firstNonEmpty]: text });
          }
          setShowConfirm(false);
          return;
        }
        if (text && (lower.includes("message") || lower.includes("body"))) {
          setMessageError(text);
          setShowConfirm(false);
          return;
        }
      }

      const errMessage = axios.isAxiosError(err)
        ? err.response?.data?.message || t("sendProposal.sendFailed")
        : t("sendProposal.sendFailed");
      toast(<ErrorToast title={t("sendProposal.errorTitle")} message={errMessage} />, {
        ...toastConfig,
        autoClose: 5000,
        icon: false,
      });
    } finally {
      setSending(false);
    }
  };

  const inputClass =
    "w-full border border-gray-200 dark:border-primary-700 rounded-lg p-3 text-sm text-primary-600 dark:text-primary-200 bg-white dark:bg-primary-800 placeholder:text-primary-300 dark:placeholder:text-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-300";

  return (
    <>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4">
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

          <div className="relative bg-white dark:bg-primary-900 rounded-2xl shadow-2xl w-full max-w-lg p-8">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-base font-semibold text-primary-700 dark:text-white">
                {t("sendProposal.title")}
              </h2>
              <button
                onClick={onClose}
                aria-label={t("sendProposal.close")}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Signatory */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-primary-700 dark:text-white mb-2">
                {t("sendProposal.signatory")}
              </h3>
              <input
                type="email"
                className={inputClass}
                value={signatoryEmail}
                onChange={(e) => {
                  setSignatoryEmail(e.target.value);
                  setSignatoryError(null);
                }}
                placeholder={t("sendProposal.signatoryPlaceholder")}
              />
              {signatoryError ? (
                <p className="text-xs text-red-600 mt-1">{signatoryError}</p>
              ) : (
                trimmedSignatory !== "" &&
                !signatoryValid && (
                  <p className="text-xs text-red-600 mt-1">{t("sendProposal.invalidEmail")}</p>
                )
              )}
            </div>

            {/* Additional recipients */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-primary-700 dark:text-white mb-1">
                {t("sendProposal.additionalRecipients")}
              </h3>
              <p className="text-xs text-primary-400 mb-2">
                {t("sendProposal.additionalRecipientsHint")}
              </p>
              <div className="space-y-2">
                {emailsLoading ? (
                  <div className={`${inputClass} flex items-center justify-center`}>
                    <Spinner />
                  </div>
                ) : (
                  additionalRecipients.map((recipient, index) => {
                    const trimmed = recipient.trim();
                    const showFormatError =
                      !recipientErrors[index] && trimmed !== "" && !isValidEmail(trimmed);
                    return (
                      <div key={index}>
                        <div className="flex items-center gap-2">
                          <input
                            type="email"
                            className={inputClass}
                            placeholder={t("sendProposal.recipientPlaceholder")}
                            value={recipient}
                            onChange={(e) => updateRecipient(index, e.target.value)}
                          />
                          <button
                            type="button"
                            onClick={() => removeRecipient(index)}
                            aria-label={t("sendProposal.removeRecipient")}
                            className="shrink-0 text-gray-400 hover:text-red-500 transition-colors p-2"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M8 7V5a2 2 0 012-2h4a2 2 0 012 2v2"
                              />
                            </svg>
                          </button>
                        </div>
                        {recipientErrors[index] && (
                          <p className="text-xs text-red-600 mt-1">{recipientErrors[index]}</p>
                        )}
                        {showFormatError && (
                          <p className="text-xs text-red-600 mt-1">{t("sendProposal.invalidEmail")}</p>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
              <button
                type="button"
                onClick={addRecipient}
                disabled={emailsLoading}
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-primary-500 hover:text-primary-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                {t("sendProposal.addRecipient")}
              </button>
            </div>

            {/* Message */}
            <div className="mb-8">
              <h3 className="text-sm font-semibold text-primary-700 dark:text-white mb-1">
                {t("sendProposal.message")}
              </h3>
              <p className="text-xs text-primary-400 mb-2">
                {t("sendProposal.messageHint")}
              </p>
              <textarea
                className="w-full border border-gray-200 dark:border-primary-700 rounded-lg p-3 text-sm text-primary-400 dark:text-primary-300 bg-white dark:bg-primary-800 focus:outline-none focus:ring-1 focus:ring-primary-300 resize-none"
                rows={4}
                placeholder={t("sendProposal.messagePlaceholder")}
                value={message}
                onChange={(e) => {
                  setMessage(e.target.value);
                  setMessageError(null);
                }}
              />
              {messageError && (
                <p className="text-xs text-red-600 mt-1">{messageError}</p>
              )}
            </div>

            {/* Send button */}
            <div className="flex justify-end">
              <button
                onClick={handleSendClick}
                disabled={!canSend}
                className="text-primary-500 font-semibold text-base hover:text-primary-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {t("sendProposal.send")}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation popup */}
      {showConfirm && (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div
              className="fixed inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => !sending && setShowConfirm(false)}
            />

            <div className="relative bg-white dark:bg-primary-900 rounded-2xl shadow-2xl w-full max-w-md p-8">
              <h2 className="text-base font-semibold text-primary-700 dark:text-white mb-4">
                {t("sendProposal.confirmTitle")}
              </h2>
              <p className="text-sm text-primary-600 dark:text-primary-200 mb-4">
                {t("sendProposal.confirmBody1")}{" "}
                <strong className="text-primary-700 dark:text-white">{signatoryEmail}</strong>.{" "}
                {t("sendProposal.confirmBody2")}
              </p>
              {additionalRecipients.some((e) => e.trim()) && (
                <div className="mb-6 text-sm text-primary-600 dark:text-primary-200">
                  <span className="font-semibold text-primary-700 dark:text-white">{t("sendProposal.cc")}</span>
                  <ul className="mt-1 space-y-0.5">
                    {additionalRecipients
                      .map((e) => e.trim())
                      .filter(Boolean)
                      .map((email) => (
                        <li key={email} className="text-primary-600 dark:text-primary-200">
                          {email}
                        </li>
                      ))}
                  </ul>
                </div>
              )}

              <div className="flex justify-end items-center gap-4">
                <button
                  onClick={() => setShowConfirm(false)}
                  disabled={sending}
                  className="text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {t("sendProposal.cancel")}
                </button>
                <button
                  onClick={handleConfirmSend}
                  disabled={sending}
                  className="text-primary-500 font-semibold text-base hover:text-primary-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {sending ? t("sendProposal.sending") : t("sendProposal.confirm")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
